/**
 * useCloneScriptUnits — "copy this question's answers onto other entity instances" for the
 * Call Script (BV-1575), the script's counterpart to the Output board's item clone
 * (`useCloneItems` / BI-1625).
 *
 * WHY IT IS NOT `useCloneItems`. The board renders `drug` as an array: one component owns
 * every item, so a clone is a full replace of `items[target]` written back as one array.
 * The script has no array container at all — `buildScriptModel` FANS an entity-scoped
 * question out into one `ScriptUnit` per instance (`questionId#index`), each holding flat
 * fields keyed absolutely (`drug[0].coverage.copay`). One drug's fields are therefore
 * spread across many units, and no unit owns the whole item.
 *
 * So the scope here is deliberately NARROWER than the board's: cloning copies only the
 * fields OF THIS QUESTION across instances, never the whole entity. In the script the rep
 * works one question at a time with the other steps collapsed; a full-item replace would
 * silently rewrite answers in units they cannot see. On the board the whole item is open
 * in front of them when they clone it, which is what makes full replace safe there.
 *
 * Clone state is keyed by `questionId` + instance index, so each question has its own
 * independent session and only one can be active at a time.
 *
 * Copy semantics: for every field of the source unit, the value is written to the same
 * relative path under each selected target index — `drug[0].coverage.copay` →
 * `drug[2].coverage.copay`. Values only: evidence and heard markers live outside
 * `output_data` and are NOT cloned, matching the board (cloned data carries no
 * source-call evidence, and a cloned heard marker would send post-call extraction hunting
 * for a value the rep never heard for that instance).
 */
import { useCallback, useMemo, useState } from 'react';

import type { UseCloneItemsResult } from '@/components/fields/useCloneItems';
import { parseArrayItemPath } from '@/utils/dispositionCompleteness';
import type { ScriptUnit } from '@/utils/humanCallModel';

/** One field write produced by a clone. */
export interface CloneWrite {
  key: string;
  value: unknown;
}

interface CloneState {
  questionId: string;
  sourceIndex: number;
  selected: Set<number>;
}

/**
 * Rewrite an entity-scoped key onto another instance index:
 * `drug[0].coverage.copay` + 2 → `drug[2].coverage.copay`. Returns null for a key that is
 * not entity-scoped, or one whose index is not `fromIndex` — both are defensive: a fanned
 * out unit's fields are all scoped to its own instance, so neither should occur.
 */
export function rekeyToInstance(key: string, fromIndex: number, toIndex: number): string | null {
  const parsed = parseArrayItemPath(key);
  if (!parsed || parsed.index !== fromIndex) return null;
  return `${parsed.array}[${toIndex}].${parsed.rest}`;
}

/**
 * Build the writes that clone `source`'s answers onto `targetIndexes`. Exported for
 * testing: it is the whole copy semantic, independent of React.
 */
export function buildCloneWrites(source: ScriptUnit, targetIndexes: number[]): CloneWrite[] {
  const fromIndex = source.instance?.index;
  if (fromIndex === undefined) return [];
  const writes: CloneWrite[] = [];
  for (const toIndex of targetIndexes) {
    if (toIndex === fromIndex) continue;
    for (const field of source.fields) {
      const key = rekeyToInstance(field.key, fromIndex, toIndex);
      if (key) writes.push({ key, value: field.value });
    }
  }
  return writes;
}

/**
 * Controllers keyed by `questionId`, shaped as `UseCloneItemsResult` so the board's
 * `CloneControls` renders them unchanged — its API is already index-based, and here the
 * index is the ENTITY INSTANCE index. A question maps to null (no control) when the column
 * is read-only or the question fanned out to fewer than two instances.
 */
export function useCloneScriptUnits(
  units: ScriptUnit[],
  editable: boolean,
  writeFields: (entries: ReadonlyArray<CloneWrite>) => void,
): Record<string, UseCloneItemsResult | null> {
  const [state, setState] = useState<CloneState | null>(null);

  // questionId → its fanned-out units, in flow order. Only entity units (those with an
  // `instance`) can be cloned; a top-level question has nothing to clone across.
  const unitsByQuestion = useMemo(() => {
    const map = new Map<string, ScriptUnit[]>();
    for (const unit of units) {
      if (!unit.instance) continue;
      const list = map.get(unit.questionId);
      if (list) list.push(unit);
      else map.set(unit.questionId, [unit]);
    }
    return map;
  }, [units]);

  const start = useCallback((questionId: string, index: number) => {
    setState({ questionId, sourceIndex: index, selected: new Set<number>() });
  }, []);

  const toggleTarget = useCallback((questionId: string, index: number) => {
    setState(prev => {
      if (!prev || prev.questionId !== questionId || index === prev.sourceIndex) return prev;
      const selected = new Set(prev.selected);
      if (selected.has(index)) selected.delete(index);
      else selected.add(index);
      return { ...prev, selected };
    });
  }, []);

  const cancel = useCallback(() => setState(null), []);

  const confirm = useCallback(() => {
    if (!state || state.selected.size === 0) {
      setState(null);
      return;
    }
    const source = unitsByQuestion
      .get(state.questionId)
      ?.find(u => u.instance?.index === state.sourceIndex);
    if (source) {
      const writes = buildCloneWrites(source, [...state.selected]);
      if (writes.length > 0) writeFields(writes);
    }
    setState(null);
  }, [state, unitsByQuestion, writeFields]);

  return useMemo(() => {
    const out: Record<string, UseCloneItemsResult | null> = {};
    for (const [questionId, questionUnits] of unitsByQuestion) {
      if (!editable || questionUnits.length < 2) {
        out[questionId] = null;
        continue;
      }
      const isThisQuestion = state?.questionId === questionId;
      out[questionId] = {
        active: isThisQuestion,
        sourceIndex: isThisQuestion ? state.sourceIndex : null,
        isSelected: (index: number) => (isThisQuestion ? state.selected.has(index) : false),
        start: (index: number) => start(questionId, index),
        toggleTarget: (index: number) => toggleTarget(questionId, index),
        selectedCount: isThisQuestion ? state.selected.size : 0,
        cancel,
        confirm,
      };
    }
    return out;
  }, [unitsByQuestion, editable, state, start, toggleTarget, cancel, confirm]);
}
